import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Button,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { FaCheckCircle, FaIndustry, FaDollarSign, FaChartLine, FaAward } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const CommercialSolarPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <NavbarWithCallToAction />

      <Box bg={getColor("background.main")} minHeight="100vh" position="relative" overflow="hidden">
        {/* Hero Section */}
        <Box
          position="relative"
          width="100vw"
          height="500px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
          backgroundImage="url('/assets/onesolarimages/abouthero.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
        >
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.5)" zIndex={1} />
          <Container maxW="1200px" position="relative" zIndex={2} height="100%" display="flex" alignItems="center">
            <VStack spacing={6} align="flex-start" maxW="800px">
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                textTransform="uppercase"
              >
                Commercial Solar
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                fontWeight="600"
              >
                Solar, A Smart Move for Your Business
              </Text>
              <Button
                size="lg"
                bg="white"
                color="#121c47"
                px={8}
                py={6}
                fontSize="md"
                fontWeight="600"
                textTransform="uppercase"
                borderRadius="4px"
                fontFamily="'Open Sans', sans-serif"
                _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                onClick={() => navigate("/contact")}
              >
                Get Commercial Quote
              </Button>
            </VStack>
          </Container>
        </Box>

        {/* Stats Banner */}
        <Box bg="#121c47" py={{ base: 8, md: 12 }}>
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <Stat textAlign="center" color="white">
                <StatNumber fontSize="4xl" fontWeight="bold">$8,000+</StatNumber>
                <StatLabel fontSize="lg">Annual Savings</StatLabel>
                <StatHelpText fontSize="sm">With 20kW system</StatHelpText>
              </Stat>
              <Stat textAlign="center" color="white">
                <StatNumber fontSize="4xl" fontWeight="bold">&lt;5 Years</StatNumber>
                <StatLabel fontSize="lg">ROI Period</StatLabel>
                <StatHelpText fontSize="sm">For 20-30kW systems</StatHelpText>
              </Stat>
              <Stat textAlign="center" color="white">
                <StatNumber fontSize="4xl" fontWeight="bold">45¢/kW</StatNumber>
                <StatLabel fontSize="lg">Feed-in Rate</StatLabel>
                <StatHelpText fontSize="sm">For excess power</StatHelpText>
              </Stat>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Main Content */}
        <Box bg="white" py={{ base: 16, md: 20 }}>
          <Container maxW="1200px">
            <VStack spacing={16} align="stretch">
              <Box>
                <Box width="60px" height="2px" bg="#121c47" mb={4} />
                <Heading as="h2" fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6}>
                  Reduce Your Business Operating Costs
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={4}>
                  Commercial businesses can significantly lower their electricity bills by investing in 20-30kW solar systems. Our efficient, affordable, and reliable solar solutions are designed to maximize your business savings while demonstrating corporate social responsibility.
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                  Our in-house team of fully qualified solar designers, installers, and master electricians ensure professional installation without subcontractors.
                </Text>
              </Box>

              {/* Benefits */}
              <Box>
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6}>
                  Commercial Solar Benefits
                </Heading>
                <List spacing={4}>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Substantial Cost Savings
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Save up to $8,000 annually with a 20kW system
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Quick Return on Investment
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          ROI under 5 years for 20-30kW systems
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Revenue from Excess Power
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Sell unused power back to providers at premium rates
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Corporate Responsibility
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Demonstrate environmental commitment to stakeholders
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Energy Independence
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Protect your business from rising electricity costs
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                </List>
              </Box>

              {/* Industries */}
              <Box>
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6}>
                  Perfect For All Industries
                </Heading>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                  <Box textAlign="center" p={4} border="1px solid" borderColor="gray.200" borderRadius="lg">
                    <FaIndustry size={40} color="#121c47" style={{ margin: '0 auto 1rem' }} />
                    <Text fontSize="md" fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                      Manufacturing
                    </Text>
                  </Box>
                  <Box textAlign="center" p={4} border="1px solid" borderColor="gray.200" borderRadius="lg">
                    <FaIndustry size={40} color="#121c47" style={{ margin: '0 auto 1rem' }} />
                    <Text fontSize="md" fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                      Retail
                    </Text>
                  </Box>
                  <Box textAlign="center" p={4} border="1px solid" borderColor="gray.200" borderRadius="lg">
                    <FaIndustry size={40} color="#121c47" style={{ margin: '0 auto 1rem' }} />
                    <Text fontSize="md" fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                      Warehouses
                    </Text>
                  </Box>
                  <Box textAlign="center" p={4} border="1px solid" borderColor="gray.200" borderRadius="lg">
                    <FaIndustry size={40} color="#121c47" style={{ margin: '0 auto 1rem' }} />
                    <Text fontSize="md" fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                      Agriculture
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* CTA */}
              <Box bg="#121c47" color="white" p={{ base: 8, md: 12 }} borderRadius="lg" textAlign="center">
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }} fontWeight="bold" fontFamily="'Open Sans', sans-serif" mb={4}>
                  Ready to Reduce Your Business Energy Costs?
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} fontFamily="'Open Sans', sans-serif" mb={6} maxW="700px" mx="auto">
                  Contact us today for a free commercial solar assessment. We'll design a system that maximizes your ROI and demonstrates your commitment to sustainability.
                </Text>
                <Button
                  size="lg"
                  bg="white"
                  color="#121c47"
                  px={8}
                  py={6}
                  fontSize="md"
                  fontWeight="600"
                  textTransform="uppercase"
                  borderRadius="4px"
                  fontFamily="'Open Sans', sans-serif"
                  _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                  onClick={() => navigate("/contact")}
                >
                  Get Your Free Quote
                </Button>
              </Box>
            </VStack>
          </Container>
        </Box>

        <FooterWithFourColumns />
      </Box>
    </>
  );
};

export default CommercialSolarPage;
